import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

import type { MessageKey } from '@/i18n/types';

export type CameraStatus = 'idle' | 'starting' | 'ready' | 'denied' | 'unavailable';

interface UseCameraResult {
  videoRef: RefObject<HTMLVideoElement | null>;
  status: CameraStatus;
  /**
   * A dictionary key rather than a sentence: translating in here would put the
   * locale in `start`'s dependencies, and the camera would restart every time
   * the language changed.
   */
  error: MessageKey | null;
  start: () => void;
  stop: () => void;
}

/**
 * Owns the camera stream for the capture screen.
 *
 * `getUserMedia` requires a secure context, which is why the local environment
 * terminates TLS rather than serving plain HTTP. The stream is stopped on
 * unmount without exception: leaving it open keeps the recording indicator lit
 * and holds the device against other apps.
 */
export function useCamera(): UseCameraResult {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  // Guards against the screen being left while the permission prompt is still
  // open — the stream then arrives with nowhere to go and must be released.
  const abandonedRef = useRef(false);
  const [status, setStatus] = useState<CameraStatus>('idle');
  const [error, setError] = useState<MessageKey | null>(null);

  const stop = useCallback(() => {
    abandonedRef.current = true;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setStatus('idle');
  }, []);

  const start = useCallback(() => {
    abandonedRef.current = false;

    void (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setStatus('unavailable');
        setError('errors.camera_unsupported');

        return;
      }

      setStatus('starting');
      setError(null);

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          // A portrait frame matching the 1080x1350 target, requested as ideal
          // so a device that cannot manage it still returns something usable.
          video: {
            facingMode: 'user',
            width: { ideal: 1080 },
            height: { ideal: 1350 },
          },
          audio: false,
        });

        if (abandonedRef.current) {
          stream.getTracks().forEach((track) => track.stop());

          return;
        }

        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }

        setStatus('ready');
      } catch (cause) {
        const name = cause instanceof DOMException ? cause.name : '';

        if (name === 'NotAllowedError' || name === 'SecurityError') {
          setStatus('denied');
          setError('errors.camera_denied');
        } else {
          setStatus('unavailable');
          setError('errors.camera_unavailable');
        }
      }
    })();
  }, []);

  useEffect(() => stop, [stop]);

  return { videoRef, status, error, start, stop };
}
